import React, { useEffect, useState, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Form, Input, Avatar, Button, message, Upload, Tooltip, Card, Row, Col, Typography, Modal, Divider, Tag, Space, Tabs, Alert, Switch, Select, Spin } from 'antd';
import { SaveOutlined, ArrowLeftOutlined, SafetyOutlined, KeyOutlined, DeleteOutlined, CopyOutlined, PlusOutlined, CloseOutlined, HolderOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { COMMERCE_NAME_LOCALE_EXTRA_KEYS, parseNameLocalesField } from '../constants/commerceNameLocales';
import { confirmDeleteCompany, loadCompanyDeletePermission } from '../utils/companyDeleteUtils';
import WA_BUSINESS_CATEGORIES from '../utils/waBusinessCategories';
import {
  GMT_OFFSET_OPTIONS,
  getGMTOffsetString,
  getTimezoneValueByGMTOffset,
  normalizeGMTOffsetString,
} from '../configs/timezones';
import zhTC from '../locales/zh-TC';
import en from '../locales/en';

/** 合併表單/原始值並 trim，避免複製貼上 WA ID 時帶入前後空白 */
const pickTrimmedWaField = (formVal, originalVal) => {
  const raw = formVal ?? originalVal;
  if (raw == null) return raw;
  const trimmed = String(raw).trim();
  return trimmed === '' ? null : trimmed;
};

const CompanyEditPage = () => {
  const [form] = Form.useForm();
  const [logoUrl, setLogoUrl] = useState('');
  const [originalData, setOriginalData] = useState(null);
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const navigate = useNavigate();
  const { Title, Text } = Typography;
  const { currentLanguage, t } = useLanguage();
  const locale = currentLanguage === 'zh-TC' ? zhTC.companyEdit : en.companyEdit;

  const [canDeleteCompany, setCanDeleteCompany] = useState(false);

  // 破冰問題 / 指令 對話元件
  const [iceBreakers, setIceBreakers] = useState([]);
  const [commands, setCommands] = useState([]);
  const [convLoading, setConvLoading] = useState(false);
  const [convLoaded, setConvLoaded] = useState(false);
  const [iceBreakerSaving, setIceBreakerSaving] = useState(false);
  const [commandSaving, setCommandSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadDeletePermission = async () => {
      const canDelete = await loadCompanyDeletePermission();
      if (!cancelled) setCanDeleteCompany(canDelete);
    };
    loadDeletePermission();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!id) return;
    fetch(`/api/companies/${id}`, {
      headers: { Authorization: 'Bearer ' + localStorage.getItem('token') }
    })
      .then(async res => {
        if (!res.ok) {
          if (res.status === 401) {
            message.error(t('companyEdit.unauthorized') || '未授權，請重新登入');
          } else if (res.status === 403) {
            message.error(t('companyEdit.forbidden') || '沒有權限查看此公司資料');
          } else if (res.status === 404) {
            message.error(t('companyEdit.notFound') || '找不到公司資料');
          } else {
            message.error(t('companyEdit.loadFailed') || '載入公司資料失敗');
          }
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        setOriginalData(data);
        setTimeout(() => {
          const nl = parseNameLocalesField(data.nameLocales ?? data.name_locales);
          form.setFieldsValue({
            name: data.name,
            nameLocales: {
              'zh-TC': nl['zh-TC'] || '',
              'zh-SC': nl['zh-SC'] || '',
            },
            description: data.description,
            email: data.email,
            address: data.address,
            phone: data.phone,
            website: data.website,
            website2: data.website2,
            wA_BusinessCategory: data.wA_BusinessCategory,
            wA_API_Key: data.wA_API_Key,
            wA_PhoneNo_ID: data.wA_PhoneNo_ID,
            wA_Business_Account_ID: data.wA_Business_Account_ID,
            wA_Meta_Business_ID: data.wA_Meta_Business_ID,
            wA_VerifyToken: data.wA_VerifyToken,
            wA_WebhookToken: data.wA_WebhookToken,
            wA_WebhookUrl: data.wA_WebhookToken ? `${window.location.origin}/api/MetaWebhook/${data.wA_WebhookToken}` : '',
            wA_Flow_PrivateKey: data.wA_Flow_PrivateKey,
            // WhatsApp 菜單設置
            wA_WelcomeMessage: data.wA_WelcomeMessage,
            wA_NoFunctionMessage: data.wA_NoFunctionMessage,
            wA_MenuTitle: data.wA_MenuTitle,
            wA_MenuFooter: data.wA_MenuFooter,
            wA_MenuButton: data.wA_MenuButton,
            wA_SectionTitle: data.wA_SectionTitle,
            wA_DefaultOptionDescription: data.wA_DefaultOptionDescription,
            wA_InputErrorMessage: data.wA_InputErrorMessage,
            wA_FallbackMessage: data.wA_FallbackMessage,
            wA_SystemErrorMessage: data.wA_SystemErrorMessage,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            logoUrl: data.logoUrl,
            isPlatformWaba: data.isPlatformWaba,
            timezone: data.timezone
              ? normalizeGMTOffsetString(getGMTOffsetString(data.timezone))
              : undefined,
          });
          setLogoUrl(data.logoUrl || '');
        }, 0);
      })
      .catch(err => {
        console.error('載入公司資料失敗:', err);
      });
  }, [form, id]);

  // 監聽 Webhook Token 變化，自動更新 URL
  useEffect(() => {
    const webhookToken = form.getFieldValue('wA_WebhookToken');
    if (webhookToken) {
      const webhookUrl = `${window.location.origin}/api/MetaWebhook/${webhookToken}`;
      form.setFieldsValue({ wA_WebhookUrl: webhookUrl });
    }
  }, [form.getFieldValue('wA_WebhookToken')]);

  const handleSave = async () => {
    try {
      const formValues = form.getFieldsValue();
      
      // 合併原始數據，確保未進入過的 tab 的字段不會丟失
      // 優先使用表單中的值（已修改的），如果沒有則使用原始數據的值
      const nl = formValues.nameLocales || {};
      const values = {
        // 基本資料
        name: formValues.name ?? originalData?.name,
        nameLocales: {
          'zh-TC': nl['zh-TC'] ?? '',
          'zh-SC': nl['zh-SC'] ?? '',
        },
        description: formValues.description ?? originalData?.description,
        email: formValues.email ?? originalData?.email,
        address: formValues.address ?? originalData?.address,
        phone: formValues.phone ?? originalData?.phone,
        website: formValues.website ?? originalData?.website,
        website2: formValues.website2 ?? originalData?.website2,
        wA_BusinessCategory: formValues.wA_BusinessCategory ?? originalData?.wA_BusinessCategory,
        logoUrl: logoUrl,
        
        // API 設置
        wA_API_Key: formValues.wA_API_Key ?? originalData?.wA_API_Key,
        wA_PhoneNo_ID: pickTrimmedWaField(formValues.wA_PhoneNo_ID, originalData?.wA_PhoneNo_ID),
        wA_Business_Account_ID: pickTrimmedWaField(formValues.wA_Business_Account_ID, originalData?.wA_Business_Account_ID),
        wA_Meta_Business_ID: pickTrimmedWaField(formValues.wA_Meta_Business_ID, originalData?.wA_Meta_Business_ID),
        wA_VerifyToken: pickTrimmedWaField(formValues.wA_VerifyToken, originalData?.wA_VerifyToken),
        wA_WebhookToken: pickTrimmedWaField(formValues.wA_WebhookToken, originalData?.wA_WebhookToken),
        wA_Flow_PrivateKey: formValues.wA_Flow_PrivateKey ?? originalData?.wA_Flow_PrivateKey,
        
        // WhatsApp 菜單設置 - 確保這些字段不會因為未進入 tab 而丟失
        wA_WelcomeMessage: formValues.wA_WelcomeMessage ?? originalData?.wA_WelcomeMessage,
        wA_NoFunctionMessage: formValues.wA_NoFunctionMessage ?? originalData?.wA_NoFunctionMessage,
        wA_MenuTitle: formValues.wA_MenuTitle ?? originalData?.wA_MenuTitle,
        wA_MenuFooter: formValues.wA_MenuFooter ?? originalData?.wA_MenuFooter,
        wA_MenuButton: formValues.wA_MenuButton ?? originalData?.wA_MenuButton,
        wA_SectionTitle: formValues.wA_SectionTitle ?? originalData?.wA_SectionTitle,
        wA_DefaultOptionDescription: formValues.wA_DefaultOptionDescription ?? originalData?.wA_DefaultOptionDescription,
        wA_InputErrorMessage: formValues.wA_InputErrorMessage ?? originalData?.wA_InputErrorMessage,
        wA_FallbackMessage: formValues.wA_FallbackMessage ?? originalData?.wA_FallbackMessage,
        wA_SystemErrorMessage: formValues.wA_SystemErrorMessage ?? originalData?.wA_SystemErrorMessage,
        isPlatformWaba: formValues.isPlatformWaba ?? originalData?.isPlatformWaba ?? false,
        timezone: (() => {
          const raw = formValues.timezone ?? originalData?.timezone;
          if (!raw) return null;
          const normalized = normalizeGMTOffsetString(raw);
          return getTimezoneValueByGMTOffset(normalized) || null;
        })(),
      };
      
      // 新增公司時，名稱必填
      if (!id && (!values.name || !String(values.name).trim())) {
        message.error(t('companyEdit.nameRequired') || '請填寫公司名稱');
        return;
      }

      const isNewCompany = !id;
      const url = isNewCompany ? '/api/companies' : `/api/companies/${id}`;
      const method = isNewCompany ? 'POST' : 'PUT';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('token') },
        body: JSON.stringify(values)
      });

      if (!response.ok) {
        const errBody = await response.text();
        let errMsg = t('companyEdit.saveFailed');
        try {
          const errJson = JSON.parse(errBody);
          if (errJson?.details) errMsg = `${errJson.error || errMsg}: ${errJson.details}`;
          else if (errJson?.error) errMsg = errJson.error;
        } catch { /* ignore */ }
        message.error(errMsg);
        return;
      }

      message.success(t('companyEdit.saveSuccess'));
      navigate('/company-user-admin');
    } catch (err) {
      console.error('保存公司失敗:', err);
      message.error(t('companyEdit.saveFailed'));
    }
  };

  const handleDelete = () => {
    if (!id) return;
    confirmDeleteCompany({
      companyId: id,
      companyName: originalData?.name || form.getFieldValue('name'),
      canDeleteCompany,
      t,
      onDeleted: () => navigate('/company-user-admin')
    });
  };

  // 驗證 Token 權限
  const handleValidateToken = async () => {
    try {
      // 獲取表單中當前的值
      const waApiKey = form.getFieldValue('wA_API_Key');
      const waBusinessAccountId = form.getFieldValue('wA_Business_Account_ID');
      const waPhoneNoId = form.getFieldValue('wA_PhoneNo_ID');

      // 驗證必填欄位
      if (!waApiKey) {
        message.warning(t('companyEdit.apiKeyRequired'));
        return;
      }

      if (!waBusinessAccountId) {
        message.warning(t('companyEdit.businessAccountIdRequired'));
        return;
      }

      message.loading(t('companyEdit.validateTokenLoading'), 0);

      const token = localStorage.getItem('token');
      const response = await fetch('/api/whatsapptokenvalidation/validate-permissions', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // 檢查 HTTP 狀態碼
      if (!response.ok) {
        message.destroy();
        const errorResult = await response.json();
        console.error('❌ Token 驗證 API 錯誤:', errorResult);
        
        Modal.error({
          title: t('companyEdit.validateTokenError'),
          content: errorResult.error || errorResult.message || t('companyEdit.validateTokenErrorMsg')
        });
        return;
      }
      
      const result = await response.json();
      message.destroy();

      // 調試：打印 API 響應
      console.log('🔍 Token 驗證 API 響應:', result);

      if (result.success) {
        Modal.info({
          title: t('companyEdit.validateTokenCheckResult'),
          width: 600,
          content: (
            <div>
              <p><strong>{t('companyEdit.tokenStatus')}</strong>{result.tokenValid ? t('companyEdit.tokenValid') : t('companyEdit.tokenInvalid')}</p>
              {result.company && <p><strong>{t('companyEdit.companyLabel')}</strong>{result.company.name}</p>}
              <Divider />
              
              {result.capabilities && (
                <>
                  <p><strong>{t('companyEdit.capabilitiesTitle')}</strong></p>
                  <ul>
                    <li>{t('companyEdit.sendMessageLabel')}{result.capabilities.canSendMessages ? t('companyEdit.available') : t('companyEdit.unavailable')}</li>
                    <li>{t('companyEdit.receiveWebhookLabel')}{result.capabilities.canReceiveWebhooks ? t('companyEdit.available') : t('companyEdit.unavailable')}</li>
                    <li>{t('companyEdit.manageTemplatesLabel')}{result.capabilities.canManageTemplates ? t('companyEdit.available') : t('companyEdit.unavailable')}</li>
                    <li>{t('companyEdit.createFlowLabel')}{result.capabilities.canCreateFlows ? t('companyEdit.available') : t('companyEdit.unavailable')}</li>
                  </ul>
                  <Divider />
                </>
              )}
              
              {result.permissions && result.permissions.length > 0 && (
                <>
                  <p><strong>{t('companyEdit.permissionsTitle')}</strong></p>
                  <ul>
                    {result.permissions.map((p, i) => (
                      <li key={i}>
                        {p.permission}: <Tag color={p.status === 'granted' ? 'green' : 'red'}>{p.status}</Tag>
                      </li>
                    ))}
                  </ul>
                  <Divider />
                </>
              )}
              
              {result.recommendations && result.recommendations.length > 0 && (
                <>
                  <p><strong>{t('companyEdit.recommendationsTitle')}</strong></p>
                  <ul>
                    {result.recommendations.map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )
        });
      } else {
        Modal.error({
          title: t('companyEdit.validateTokenFailed'),
          content: result.error || t('companyEdit.permissionCheckFailed')
        });
      }
    } catch (error) {
      message.destroy();
      Modal.error({
        title: t('companyEdit.validateTokenError'),
        content: error.message || t('companyEdit.validateTokenCheckError')
      });
    }
  };

  // 訂閱 WhatsApp Webhook
  const handleSubscribeWebhook = async () => {
    try {
      const waApiKey = form.getFieldValue('wA_API_Key');
      const waBusinessAccountId = form.getFieldValue('wA_Business_Account_ID');

      if (!waApiKey) {
        message.warning(t('companyEdit.apiKeyRequired'));
        return;
      }

      if (!waBusinessAccountId) {
        message.warning(t('companyEdit.businessAccountIdRequired'));
        return;
      }

      message.loading(t('companyEdit.subscribeWebhookLoading'), 0);

      const token = localStorage.getItem('token');
      const response = await fetch('/api/whatsapptokenvalidation/subscribe-webhook', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const responseText = await response.text();
      message.destroy();

      let result = null;
      try {
        result = responseText ? JSON.parse(responseText) : null;
      } catch {
        result = null;
      }

      if (response.ok && result?.success) {
        Modal.success({
          title: t('companyEdit.subscribeWebhookSuccessTitle'),
          content: result.message || t('companyEdit.subscribeWebhookSuccess')
        });
      } else {
        Modal.error({
          title: t('companyEdit.subscribeWebhookErrorTitle'),
          content: result?.error || result?.message || responseText || t('companyEdit.subscribeWebhookError')
        });
      }
    } catch (error) {
      message.destroy();
      Modal.error({
        title: t('companyEdit.subscribeWebhookErrorTitle'),
        content: error.message || t('companyEdit.subscribeWebhookError')
      });
    }
  };

  // 上傳公司頭像
  const handleUpload = async ({ file }) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post('/api/companies/logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: 'Bearer ' + token
        }
      });
      if (res.data.url) {
        setLogoUrl(res.data.url);
        message.success(t('companyEdit.uploadSuccess'));
      } else {
        message.error(t('companyEdit.uploadFailed'));
      }
    } catch {
      message.error(t('companyEdit.uploadFailed'));
    }
  };

  // 生成 Flow 金鑰
  const handleGenerateKeys = async () => {
    try {
      Modal.confirm({
        title: t('companyEdit.generateKeyConfirmTitle') || '確認生成新金鑰?',
        content: t('companyEdit.generateKeyConfirmContent') || '這將會覆蓋現有的私鑰，並且您需要將新的公鑰重新上傳到 Meta。確定要繼續嗎?',
        onOk: async () => {
          const hide = message.loading('正在生成金鑰...', 0);
          try {
            const token = localStorage.getItem('token');
            const res = await fetch(`/api/companies/${id}/generate-flow-keys`, {
              method: 'POST',
              headers: { Authorization: 'Bearer ' + token }
            });
            const data = await res.json();
            hide();
            
            if (res.ok && data.success) {
              // 刷新表單中的私鑰 (雖然通常後端不返回私鑰，但為了即時體驗，我們重新獲取一次公司詳情或直接信任後端已保存)
              // 這裡我們重新獲取公司詳情來更新 UI
              const companyRes = await fetch(`/api/companies/${id}`, {
                 headers: { Authorization: 'Bearer ' + token }
              });
              const companyData = await companyRes.json();
              form.setFieldValue('wA_Flow_PrivateKey', companyData.wA_Flow_PrivateKey);
              
              // 顯示公鑰給用戶複製
              Modal.success({
                title: '金鑰生成成功',
                width: 600,
                content: (
                  <div>
                    <p>私鑰已安全保存到系統。</p>
                    <p><strong>請複製以下公鑰並上傳到 Meta WhatsApp Manager 的 Flow Endpoint 設置中：</strong></p>
                    <Input.TextArea 
                      value={data.publicKey} 
                      rows={6} 
                      readOnly 
                      style={{ fontFamily: 'monospace', background: '#f5f5f5' }} 
                    />
                    <Button 
                      type="primary" 
                      style={{ marginTop: 16 }}
                      onClick={() => {
                        navigator.clipboard.writeText(data.publicKey);
                        message.success('公鑰已複製');
                      }}
                    >
                      複製公鑰
                    </Button>
                  </div>
                )
              });
            } else {
              message.error('生成失敗: ' + (data.error || '未知錯誤'));
            }
          } catch (e) {
            hide();
            message.error('生成失敗: ' + e.message);
          }
        }
      });
    } catch (e) {
      console.error(e);
    }
  };

  // 讀取破冰問題 / 指令
  const loadConversationalAutomation = async () => {
    if (!id) return;
    setConvLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/whatsapp/conversational-automation?companyId=${id}`, {
        headers: { Authorization: 'Bearer ' + token }
      });
      if (res.ok) {
        const data = await res.json();
        setIceBreakers(data.prompts || []);
        setCommands(data.commands || []);
        setConvLoaded(true);
      } else {
        const err = await res.json().catch(() => ({}));
        message.error(err.error || t('companyEdit.convLoadFailed'));
      }
    } catch (e) {
      message.error(t('companyEdit.convLoadFailed') + ': ' + e.message);
    } finally {
      setConvLoading(false);
    }
  };

  const handleTabChange = (key) => {
    if ((key === '3' || key === '4') && !convLoaded) {
      loadConversationalAutomation();
    }
  };

  const saveConversational = async ({ type, successMsg }) => {
    if (!id) return;
    const setter = type === 'iceBreaker' ? setIceBreakerSaving : setCommandSaving;
    setter(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/whatsapp/conversational-automation?companyId=${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ prompts: iceBreakers, commands })
      });
      if (res.ok) {
        message.success(successMsg);
      } else {
        const err = await res.json().catch(() => ({}));
        message.error(err.error || t('companyEdit.convSaveFailed'));
      }
    } catch (e) {
      message.error(t('companyEdit.convSaveFailed') + ': ' + e.message);
    } finally {
      setter(false);
    }
  };

  return (
    <div style={{ 
      width: '100%',
      height: '100%',
      overflow: 'visible'
    }}>
      <div style={{ 
        maxWidth: 1100, 
        margin: '32px auto', 
        padding: '16px 16px 64px 16px',
        position: 'relative'
      }}>
      <Row gutter={[32, 16]} justify="center" align="top">
        {/* 左側：公司基本資料 */}
        <Col xs={24} md={15}>
          <Card
            style={{ borderRadius: 16, boxShadow: '0 2px 12px #eee', minHeight: 600 }}
            bodyStyle={{ padding: 32 }}
          >
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <Upload
                showUploadList={false}
                customRequest={handleUpload}
                accept="image/*"
              >
                <Avatar
                  src={logoUrl}
                  size={96}
                  style={{ marginBottom: 8, cursor: 'pointer', background: '#f0f0f0' }}
                  icon={!logoUrl && <span style={{ fontSize: 48, color: '#bbb' }}>+</span>}
                />
                <div style={{ color: '#888', fontSize: 14, fontWeight: 500 }}>{t('companyEdit.uploadLogo')}</div>
              </Upload>
            </div>
            <Title level={5} style={{ textAlign: 'center', marginBottom: 24, letterSpacing: 2, color: '#7234CF' }}>{t('companyEdit.basicInfo')}</Title>
            <Form form={form} layout="vertical">
              {id ? (
                <Form.Item label={<span style={{ fontWeight: 600 }}>{t('companyEdit.companyId')}</span>}>
                  <Input
                    readOnly
                    value={id}
                    style={{ width: '100%' }}
                    suffix={
                      <Tooltip title={t('companyEdit.copyCompanyId')}>
                        <Button
                          type="text"
                          size="small"
                          icon={<CopyOutlined />}
                          aria-label={t('companyEdit.copyCompanyId')}
                          onClick={() => {
                            navigator.clipboard.writeText(String(id));
                            message.success(t('companyEdit.companyIdCopied'));
                          }}
                        />
                      </Tooltip>
                    }
                  />
                </Form.Item>
              ) : null}
              <Form.Item name="name" label={<span style={{ fontWeight: 600 }}>{t('companyEdit.name')}</span>}>
                <Input style={{ width: '100%' }} />
              </Form.Item>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>{t('companyEdit.nameLocalesHint')}</Text>
              <Row gutter={12}>
                {COMMERCE_NAME_LOCALE_EXTRA_KEYS.map(({ jsonKey, formPath }) => (
                  <Col span={12} key={jsonKey}>
                    <Form.Item
                      name={formPath}
                      label={(
                        <span>
                          <Tooltip title={jsonKey}>
                            <Tag>{jsonKey === 'zh-TC' ? '繁' : '简'}</Tag>
                          </Tooltip>
                          {jsonKey === 'zh-TC' ? t('companyEdit.nameLocaleZhTc') : t('companyEdit.nameLocaleZhSc')}
                        </span>
                      )}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                ))}
              </Row>
              <Form.Item name="wA_BusinessCategory" label={<span style={{ fontWeight: 600 }}>行業類別</span>}>
                <Select
                  placeholder="選擇類別"
                  allowClear
                  style={{ width: '100%' }}
                >
                  {WA_BUSINESS_CATEGORIES.map(c => (
                    <Select.Option key={c.value} value={c.value}>{c.label}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="description" label={<span style={{ fontWeight: 600 }}>說明 / 關於</span>}>
                <Input.TextArea rows={3} maxLength={512} showCount placeholder="簡短描述您的業務" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="email" label={<span style={{ fontWeight: 600 }}>{t('companyEdit.email')}</span>}>
                <Input style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="address" label={<span style={{ fontWeight: 600 }}>{t('companyEdit.address')}</span>}>
                <Input style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="phone" label={<span style={{ fontWeight: 600 }}>{t('companyEdit.phone')}</span>}>
                <Input style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="website" label={<span style={{ fontWeight: 600 }}>{t('companyEdit.website')}</span>}>
                <Input style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="website2" label={<span style={{ fontWeight: 600 }}>網站 2</span>}>
                <Input style={{ width: '100%' }} placeholder="https://www.example2.com（選填）" />
              </Form.Item>
              <Form.Item name="timezone" label={<span style={{ fontWeight: 600 }}>{t('companyEdit.timezone') || '時區'}</span>}>
                <Select
                  placeholder={t('companyEdit.timezonePlaceholder') || '選擇時區'}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  style={{ width: '100%' }}
                  options={GMT_OFFSET_OPTIONS}
                />
              </Form.Item>
              <Form.Item name="createdAt" label={<span style={{ fontWeight: 600 }}>{t('companyEdit.createdAt')}</span>}>
                <Input disabled style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="updatedAt" label={<span style={{ fontWeight: 600 }}>{t('companyEdit.updatedAt')}</span>}>
                <Input disabled style={{ width: '100%' }} />
              </Form.Item>
            </Form>
          </Card>
        </Col>
        {/* 右側：WhatsApp 設定 */}
        <Col xs={24} md={9}>
          <Card
            style={{ 
              borderRadius: 16, 
              boxShadow: '0 2px 12px #eee',
              minHeight: 600
            }}
            bodyStyle={{ 
              padding: 24
            }}
          >
            <Title level={5} style={{ color: '#7234CF', marginBottom: 24, letterSpacing: 2 }}>{t('companyEdit.whatsappSetting')}</Title>
            
            <Tabs
              defaultActiveKey="1"
              onChange={handleTabChange}
              items={[
                {
                  key: '1',
                  label: t('companyEdit.apiSettings'),
                  children: (
                    <Form form={form} layout="vertical" style={{ paddingRight: '8px' }}>
                      <Form.Item name="wA_API_Key" label={<span style={{ fontWeight: 600 }}>{t('companyEdit.waApiKey')}</span>}>
                        <Input style={{ width: '100%' }} />
                      </Form.Item>
                      <Form.Item name="wA_PhoneNo_ID" label={<span style={{ fontWeight: 600 }}>{t('companyEdit.waPhoneNoId')}</span>}>
                        <Input style={{ width: '100%' }} placeholder={t('companyEdit.phoneNoIdPlaceholder')} />
                      </Form.Item>
                      <Form.Item name="wA_Business_Account_ID" label={<span style={{ fontWeight: 600 }}>{t('companyEdit.waBusinessAccountId')}</span>}>
                        <Input 
                          style={{ width: '100%' }} 
                          placeholder={t('companyEdit.businessAccountIdPlaceholder')}
                          suffix={
                            <Tooltip title={t('companyEdit.waBusinessAccountIdTooltip')}>
                              <span style={{ color: '#666', fontSize: '12px' }}>{t('companyEdit.waBusinessAccountIdSuffix')}</span>
                            </Tooltip>
                          }
                        />
                      </Form.Item>
                      <Form.Item name="wA_Meta_Business_ID" label={<span style={{ fontWeight: 600 }}>{t('companyEdit.waMetaBusinessId')}</span>}>
                        <Input 
                          style={{ width: '100%' }} 
                          placeholder={t('companyEdit.waMetaBusinessIdPlaceholder')}
                          suffix={
                            <Tooltip title={t('companyEdit.waMetaBusinessIdTooltip')}>
                              <span style={{ color: '#666', fontSize: '12px' }}>{t('companyEdit.waMetaBusinessIdSuffix')}</span>
                            </Tooltip>
                          }
                        />
                      </Form.Item>
                      
                      {canDeleteCompany && (
                        <Form.Item
                          name="isPlatformWaba"
                          label={<span style={{ fontWeight: 600 }}>Platform WABA</span>}
                          valuePropName="checked"
                          tooltip="啟用後，此公司的 WABA 憑證將自動提供給新註冊的公司使用"
                        >
                          <Switch checkedChildren="ON" unCheckedChildren="OFF" />
                        </Form.Item>
                      )}

                      {/* 重要警告提示 */}
                      <Alert
                        message={t('companyEdit.configWarningTitle')}
                        description={
                          <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                            <div style={{ marginBottom: '4px' }}>
                              <strong>{t('companyEdit.configWarningDescription')}</strong>
                            </div>
                            <div style={{ marginBottom: '4px' }}>
                              {t('companyEdit.configWarningPhoneIdUse')}
                            </div>
                            <div style={{ marginBottom: '4px' }}>
                              {t('companyEdit.configWarningBusinessIdUse')}
                            </div>
                            <div style={{ color: '#ff4d4f', marginTop: '8px' }}>
                              {t('companyEdit.configWarningMismatchTitle')}
                              <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                                <li>{t('companyEdit.configWarningMismatchItem1')}</li>
                                <li>{t('companyEdit.configWarningMismatchItem2')}</li>
                              </ul>
                            </div>
                            <div style={{ marginTop: '8px', color: '#1890ff' }}>
                              {t('companyEdit.configWarningSolution')}
                            </div>
                          </div>
                        }
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16, marginTop: 8 }}
                      />
                      
                      {/* 驗證 Token 權限按鈕 */}
                      <Form.Item>
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Button 
                            icon={<SafetyOutlined />}
                            onClick={handleValidateToken}
                            style={{ width: '100%' }}
                            type="dashed"
                          >
                            {t('companyEdit.validateTokenButton')}
                          </Button>
                          <Button 
                            onClick={handleSubscribeWebhook}
                            style={{ width: '100%' }}
                            type="primary"
                          >
                            {t('companyEdit.subscribeWebhookButton')}
                          </Button>
                        </Space>
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                          {t('companyEdit.validateTokenHint')}
                        </div>
                      </Form.Item>
                      
                      <Divider style={{ margin: '16px 0' }} />
                      
                      <Form.Item name="wA_VerifyToken" label={<span style={{ fontWeight: 600 }}>{t('companyEdit.waVerifyToken')}</span>}>
                        <Input style={{ width: '100%' }} />
                      </Form.Item>
                      <Form.Item name="wA_WebhookToken" label={<span style={{ fontWeight: 600 }}>{t('companyEdit.webhookToken')}</span>}>
                        <Input 
                          style={{ width: '100%' }} 
                          placeholder={t('companyEdit.webhookTokenPlaceholder')}
                          suffix={
                            <Button 
                              type="text" 
                              size="small" 
                              onClick={() => {
                                const newToken = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
                                form.setFieldsValue({ 
                                  wA_WebhookToken: newToken,
                                  wA_WebhookUrl: `${window.location.origin}/api/MetaWebhook/${newToken}`
                                });
                                message.success(t('companyEdit.tokenGenerated'));
                              }}
                            >
                              {t('companyEdit.regenerateToken')}
                            </Button>
                          }
                        />
                      </Form.Item>
                      <Form.Item name="wA_WebhookUrl" label={<span style={{ fontWeight: 600 }}>{t('companyEdit.webhookUrl')}</span>}>
                        <Input 
                          style={{ width: '100%' }} 
                          placeholder={t('companyEdit.webhookUrlPlaceholder')}
                          suffix={
                            <Button 
                              type="text" 
                              size="small" 
                              onClick={() => {
                                const webhookUrl = form.getFieldValue('wA_WebhookUrl');
                                if (webhookUrl) {
                                  navigator.clipboard.writeText(webhookUrl);
                                  message.success(t('companyEdit.webhookUrlCopied'));
                                } else {
                                  message.error(t('companyEdit.generateTokenFirst'));
                                }
                              }}
                            >
                              {t('companyEdit.copyWebhookUrl')}
                            </Button>
                          }
                        />
                      </Form.Item>

                      <Divider style={{ margin: '16px 0' }} />
                      
                      <Form.Item 
                        label={<span style={{ fontWeight: 600 }}>WhatsApp Flow Private Key</span>}
                        tooltip="用於解密 WhatsApp Flows 數據的 RSA 私鑰 (PEM 格式)"
                        style={{ marginBottom: 0 }}
                      >
                         <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                            <span style={{ fontSize: 12, color: '#888' }}>
                               此私鑰用於解密來自 Meta 的請求。
                            </span>
                            <Button 
                                size="small" 
                                icon={<KeyOutlined />} 
                                type="primary" 
                                ghost
                                onClick={handleGenerateKeys}
                            >
                                自動生成金鑰
                            </Button>
                         </div>
                      </Form.Item>
                      <Form.Item name="wA_Flow_PrivateKey">
                        <Input.TextArea 
                          rows={4} 
                          placeholder="-----BEGIN PRIVATE KEY----- ... -----END PRIVATE KEY-----" 
                          style={{ width: '100%', fontFamily: 'monospace' }} 
                        />
                      </Form.Item>
                    </Form>
                  ),
                },
                {
                  key: '2',
                  label: t('companyEdit.chatbotMenuConfig'),
                  children: (
                    <Form form={form} layout="vertical" style={{ paddingRight: '8px' }}>
                      <div style={{ marginBottom: '16px', padding: '12px', background: '#f6f8fa', borderRadius: '6px', fontSize: '12px', color: '#666' }}>
                        💡 {t('companyEdit.menuConfigHint')}
                      </div>
                      
                      <Form.Item 
                        name="wA_WelcomeMessage" 
                        label={<span style={{ fontWeight: 600 }}>{t('companyEdit.welcomeMessage')}</span>}
                        tooltip={t('companyEdit.welcomeMessageTooltip')}
                      >
                        <Input.TextArea 
                          rows={3} 
                          placeholder={t('companyEdit.welcomeMessagePlaceholder')}
                          style={{ width: '100%' }} 
                        />
                      </Form.Item>

                      <Form.Item 
                        name="wA_NoFunctionMessage" 
                        label={<span style={{ fontWeight: 600 }}>{t('companyEdit.noFunctionMessage')}</span>}
                        tooltip={t('companyEdit.noFunctionMessageTooltip')}
                      >
                        <Input.TextArea 
                          rows={3} 
                          placeholder={t('companyEdit.noFunctionMessagePlaceholder')}
                          style={{ width: '100%' }} 
                        />
                      </Form.Item>

                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item 
                            name="wA_MenuTitle" 
                            label={<span style={{ fontWeight: 600 }}>{t('companyEdit.menuTitle')}</span>}
                            tooltip={t('companyEdit.menuTitleTooltip')}
                          >
                            <Input placeholder={t('companyEdit.menuTitlePlaceholder')} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item 
                            name="wA_MenuButton" 
                            label={<span style={{ fontWeight: 600 }}>{t('companyEdit.menuButton')}</span>}
                            tooltip={t('companyEdit.menuButtonTooltip')}
                          >
                            <Input placeholder={t('companyEdit.menuButtonPlaceholder')} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item 
                        name="wA_MenuFooter" 
                        label={<span style={{ fontWeight: 600 }}>{t('companyEdit.menuFooter')}</span>}
                        tooltip={t('companyEdit.menuFooterTooltip')}
                      >
                        <Input placeholder={t('companyEdit.menuFooterPlaceholder')} style={{ width: '100%' }} />
                      </Form.Item>

                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item 
                            name="wA_SectionTitle" 
                            label={<span style={{ fontWeight: 600 }}>{t('companyEdit.sectionTitle')}</span>}
                            tooltip={t('companyEdit.sectionTitleTooltip')}
                          >
                            <Input placeholder={t('companyEdit.sectionTitlePlaceholder')} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item 
                            name="wA_DefaultOptionDescription" 
                            label={<span style={{ fontWeight: 600 }}>{t('companyEdit.defaultOptionDescription')}</span>}
                            tooltip={t('companyEdit.defaultOptionDescriptionTooltip')}
                          >
                            <Input placeholder={t('companyEdit.defaultOptionDescriptionPlaceholder')} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item 
                        name="wA_InputErrorMessage" 
                        label={<span style={{ fontWeight: 600 }}>{t('companyEdit.inputErrorMessage')}</span>}
                        tooltip={t('companyEdit.inputErrorMessageTooltip')}
                      >
                        <Input.TextArea 
                          rows={2} 
                          placeholder={t('companyEdit.inputErrorMessagePlaceholder')}
                          style={{ width: '100%' }} 
                        />
                      </Form.Item>

                      <Form.Item 
                        name="wA_FallbackMessage" 
                        label={<span style={{ fontWeight: 600 }}>{t('companyEdit.fallbackMessage')}</span>}
                        tooltip={t('companyEdit.fallbackMessageTooltip')}
                      >
                        <Input.TextArea 
                          rows={2} 
                          placeholder={t('companyEdit.fallbackMessagePlaceholder')}
                          style={{ width: '100%' }} 
                        />
                      </Form.Item>

                      <Form.Item 
                        name="wA_SystemErrorMessage" 
                        label={<span style={{ fontWeight: 600 }}>{t('companyEdit.systemErrorMessage')}</span>}
                        tooltip={t('companyEdit.systemErrorMessageTooltip')}
                      >
                        <Input.TextArea 
                          rows={2} 
                          placeholder={t('companyEdit.systemErrorMessagePlaceholder')}
                          style={{ width: '100%' }} 
                        />
                      </Form.Item>
                    </Form>
                  ),
                },
                {
                  key: '3',
                  label: t('companyEdit.iceBreakersTab'),
                  children: (
                    <Spin spinning={convLoading}>
                      <div style={{ fontSize: 13, color: '#666', marginBottom: 16, lineHeight: 1.6 }}>
                        {t('companyEdit.iceBreakersDesc')}
                      </div>

                      {!id ? (
                        <Alert message={t('companyEdit.convNotSaved')} type="info" showIcon />
                      ) : (
                        <>
                          {iceBreakers.map((prompt, index) => (
                            <div
                              key={index}
                              style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 10, gap: 6 }}
                            >
                              <HolderOutlined style={{ color: '#ccc', marginTop: 30, cursor: 'grab', flexShrink: 0 }} />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 11, color: '#888', fontWeight: 600, marginBottom: 3 }}>
                                  {t('companyEdit.iceBreakersLabel', { index: index + 1 })}
                                </div>
                                <Input
                                  value={prompt}
                                  maxLength={80}
                                  showCount
                                  placeholder={t('companyEdit.iceBreakersInputPlaceholder')}
                                  onChange={e => {
                                    const next = [...iceBreakers];
                                    next[index] = e.target.value;
                                    setIceBreakers(next);
                                  }}
                                />
                              </div>
                              <Button
                                type="text"
                                icon={<CloseOutlined />}
                                style={{ marginTop: 24, flexShrink: 0 }}
                                onClick={() => setIceBreakers(iceBreakers.filter((_, i) => i !== index))}
                              />
                            </div>
                          ))}

                          {iceBreakers.length < 4 && (
                            <Button
                              type="dashed"
                              icon={<PlusOutlined />}
                              onClick={() => setIceBreakers([...iceBreakers, ''])}
                              style={{ width: '100%', marginTop: 4, marginBottom: 4 }}
                            >
                              {t('companyEdit.addIceBreaker')}
                            </Button>
                          )}

                          <Divider style={{ margin: '16px 0' }} />
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Button onClick={loadConversationalAutomation}>{t('companyEdit.cancel')}</Button>
                            <Button
                              type="primary"
                              loading={iceBreakerSaving}
                              onClick={() => saveConversational({ type: 'iceBreaker', successMsg: t('companyEdit.saveIceBreakersSuccess') })}
                            >
                              {t('companyEdit.save')}
                            </Button>
                          </div>
                        </>
                      )}
                    </Spin>
                  ),
                },
                {
                  key: '4',
                  label: t('companyEdit.commandsTab'),
                  children: (
                    <Spin spinning={convLoading}>
                      <div style={{ fontSize: 13, color: '#666', marginBottom: 16, lineHeight: 1.6 }}>
                        {t('companyEdit.commandsDesc')}
                      </div>

                      {!id ? (
                        <Alert message={t('companyEdit.convNotSaved')} type="info" showIcon />
                      ) : (
                        <>
                          {commands.map((cmd, index) => (
                            <div
                              key={index}
                              style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 10, gap: 6 }}
                            >
                              <HolderOutlined style={{ color: '#ccc', marginTop: 12, cursor: 'grab', flexShrink: 0 }} />
                              <div style={{ flex: 1, border: '1px solid #e8e8e8', borderRadius: 8, padding: '10px 12px' }}>
                                <div style={{ marginBottom: 8 }}>
                                  <div style={{ fontSize: 11, color: '#888', fontWeight: 600, marginBottom: 3 }}>{t('companyEdit.cmdNameLabel')}</div>
                                  <Input
                                    value={cmd.command_name}
                                    maxLength={32}
                                    showCount
                                    placeholder={t('companyEdit.cmdNamePlaceholder')}
                                    onChange={e => {
                                      const next = [...commands];
                                      next[index] = { ...cmd, command_name: e.target.value };
                                      setCommands(next);
                                    }}
                                  />
                                </div>
                                <div>
                                  <div style={{ fontSize: 11, color: '#888', fontWeight: 600, marginBottom: 3 }}>{t('companyEdit.cmdDescLabel')}</div>
                                  <Input.TextArea
                                    value={cmd.command_description}
                                    maxLength={256}
                                    showCount
                                    rows={2}
                                    placeholder={t('companyEdit.cmdDescPlaceholder')}
                                    onChange={e => {
                                      const next = [...commands];
                                      next[index] = { ...cmd, command_description: e.target.value };
                                      setCommands(next);
                                    }}
                                  />
                                </div>
                              </div>
                              <Button
                                type="text"
                                icon={<CloseOutlined />}
                                style={{ marginTop: 8, flexShrink: 0 }}
                                onClick={() => setCommands(commands.filter((_, i) => i !== index))}
                              />
                            </div>
                          ))}

                          {commands.length < 30 && (
                            <Button
                              type="dashed"
                              icon={<PlusOutlined />}
                              onClick={() => setCommands([...commands, { command_name: '', command_description: '' }])}
                              style={{ width: '100%', marginTop: 4, marginBottom: 4 }}
                            >
                              {t('companyEdit.addCommand')}
                            </Button>
                          )}

                          <Divider style={{ margin: '16px 0' }} />
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Button onClick={loadConversationalAutomation}>{t('companyEdit.cancel')}</Button>
                            <Button
                              type="primary"
                              loading={commandSaving}
                              onClick={() => saveConversational({ type: 'command', successMsg: t('companyEdit.saveCommandsSuccess') })}
                            >
                              {t('companyEdit.save')}
                            </Button>
                          </div>
                        </>
                      )}
                    </Spin>
                  ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
      {/* 左上角返回與儲存按鈕 */}
      <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 10, display: 'flex', gap: 8 }}>
        <Tooltip title={t('companyEdit.back')}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/company-user-admin')}
            type="primary"
            size="middle"
            style={{ borderRadius: 8, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
            aria-label={t('companyEdit.back')}
          />
        </Tooltip>
        <Tooltip title={t('companyEdit.save')}>
          <Button
            icon={<SaveOutlined />}
            onClick={handleSave}
            type="primary"
            size="middle"
            style={{ borderRadius: 8, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
            aria-label={t('companyEdit.save')}
          />
        </Tooltip>
        {id && canDeleteCompany && (
          <Tooltip title={t('companyEdit.delete') || '刪除公司'}>
            <Button
              icon={<DeleteOutlined />}
              onClick={handleDelete}
              danger
              size="middle"
              style={{ borderRadius: 8, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
              aria-label={t('companyEdit.delete') || '刪除公司'}
            />
          </Tooltip>
        )}
      </div>
      </div>
    </div>
  );
};

export default CompanyEditPage; 